// El estado vacío de Estilos — ADR-2026-10-19-el-vacio-de-estilos-muestra-el-resultado.
//
// Cuando la marca todavía no tiene carpetas, "Nueva carpeta" sola no dice para qué sirve
// armar una. Esto lo muestra con un ejemplo de antes y después —unas referencias de estilo
// más un producto, y la foto que sale con esa estética— y lo cuenta en tres pasos.
//
// El ejemplo es de fotos que ya están en el repo y comparten la estética: el patio soleado y
// el living con luz dorada (`ambientes/`, achicadas en `estilos_vacio/`), la camisa colgada
// del alta de producto y la misma camisa puesta contra una pared blanca con sombras de
// plantas (`onboarding/generation/camisa/lifestyle.jpg`).

import { useState } from 'react';
import { ArrowRight, FolderPlus, Loader2, Plus, Sparkles } from 'lucide-react';

const EJEMPLO = {
  referencias: ['/images/estilos_vacio/referencia-patio.jpg', '/images/estilos_vacio/referencia-living.jpg'],
  producto: '/images/onboarding/product/examples/camisa-hombre.png',
  resultado: '/images/onboarding/generation/camisa/lifestyle.jpg',
};

/** La paleta de las dos referencias: crema, arena, terracota y oliva. */
const PALETA = ['#F3EADF', '#DCC3A1', '#C08A5B', '#7D8763'];

const PASOS = [
  { titulo: 'Creá una carpeta', detalle: 'Una por idea: «Verano 26», «Editorial blanco y negro».' },
  { titulo: 'Sumale fotos que te inspiren', detalle: 'Entre 5 y 15 que definan el look: lugares, luz, colores, campañas.' },
  { titulo: 'Elegila cuando crees', detalle: 'Atlas toma esa estética y la aplica a tus productos.' },
];

/** Una foto del ejemplo con su rótulo abajo. */
const Figura: React.FC<{ rotulo: string; children: React.ReactNode }> = ({ rotulo, children }) => (
  <figure className="flex shrink-0 flex-col items-center gap-2">
    {children}
    <figcaption className="text-[11.5px] font-semibold text-[#02062B]/[0.55] md:text-[12.5px]">{rotulo}</figcaption>
  </figure>
);

/**
 * Crear la primera carpeta ADENTRO del vacío: el nombre y el botón, debajo de lo que
 * explica. Era una tarjeta suelta debajo de todo, y quedaba lejos de lo que invitaba a
 * hacerla. Lo que pasa después (abrirla para sumarle fotos) lo decide quien la crea.
 */
const CrearPrimeraCarpeta: React.FC<{ onCrear: (nombre: string) => Promise<void> }> = ({ onCrear }) => {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const limpio = nombre.trim();
  const crear = async () => {
    if (!limpio || guardando) return;
    setGuardando(true);
    try {
      await onCrear(limpio);
    } finally {
      setGuardando(false);
    }
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void crear();
      }}
      className="mx-auto mt-4 flex w-full max-w-[460px] flex-col gap-2 sm:flex-row"
    >
      <input
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        onKeyDown={(e) => {
          // Con algo escrito, Escape lo borra y nada más: el selector cierra entero con el
          // Escape que llega a la ventana.
          if (e.key === 'Escape' && nombre) {
            e.stopPropagation();
            setNombre('');
          }
        }}
        maxLength={60}
        placeholder="Nombre de la carpeta, ej. Verano 26"
        aria-label="Nombre de la carpeta"
        disabled={guardando}
        // 16px en el teléfono: por debajo, iOS zoomea la pantalla al enfocar.
        className="min-w-0 flex-1 rounded-full border border-[#02062B]/[0.12] bg-white px-4 py-2.5 text-[16px] text-brand-black outline-none transition-colors placeholder:text-[#02062B]/[0.38] focus:border-brand-primary sm:text-[14px]"
      />
      <button
        type="submit"
        disabled={!limpio || guardando}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-brand-primary px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {guardando ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <FolderPlus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
        )}
        Crear carpeta
      </button>
    </form>
  );
};

export const VacioDeEstilos: React.FC<{
  /** Crea la carpeta con ese nombre. Sin esto, el vacío sólo explica. */
  onCrear?: (nombre: string) => Promise<void>;
  /** En una columna angosta (el panel lateral del modo Modelo): los pasos, uno abajo del otro. */
  angosto?: boolean;
}> = ({ onCrear, angosto = false }) => (
  <div className="mb-5 rounded-2xl bg-[#F5F7FD] px-3 py-5 md:px-6 md:py-6">
    {/* ── Antes y después: el estilo + el producto → la foto con esa estética. */}
    <div className="flex items-center justify-center gap-1.5 md:gap-4" aria-hidden="true">
      <Figura rotulo="Tu estilo">
        {/* Dos referencias en abanico y su paleta debajo: se lee como una carpeta de fotos. */}
        <div className="relative h-[104px] w-[86px] md:h-[150px] md:w-[128px]">
          <img
            src={EJEMPLO.referencias[0]}
            alt=""
            className="absolute left-0 top-0 h-[72px] w-[62px] -rotate-6 rounded-lg object-cover shadow-md ring-2 ring-white md:h-[104px] md:w-[90px]"
          />
          <img
            src={EJEMPLO.referencias[1]}
            alt=""
            className="absolute right-0 top-3 h-[72px] w-[62px] rotate-6 rounded-lg object-cover shadow-md ring-2 ring-white md:top-4 md:h-[104px] md:w-[90px]"
          />
          <div className="absolute inset-x-1 bottom-0 flex h-[18px] overflow-hidden rounded-md ring-2 ring-white md:h-[24px]">
            {PALETA.map((color) => (
              <span key={color} className="flex-1" style={{ background: color }} />
            ))}
          </div>
        </div>
      </Figura>
      <Plus className="mb-6 h-4 w-4 shrink-0 text-[#02062B]/[0.35] md:h-5 md:w-5" strokeWidth={2.5} />
      <Figura rotulo="Tu producto">
        <img
          src={EJEMPLO.producto}
          alt=""
          className="h-[104px] w-[66px] rounded-lg bg-white object-cover shadow-sm ring-1 ring-black/[0.06] md:h-[150px] md:w-[100px]"
        />
      </Figura>
      {/* La flecha del antes al después. */}
      <span className="mb-6 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-primary text-white shadow-md md:h-9 md:w-9">
        <ArrowRight className="h-4 w-4 md:h-5 md:w-5" strokeWidth={2.5} />
      </span>
      <Figura rotulo="El resultado">
        <div className="relative">
          <img
            src={EJEMPLO.resultado}
            alt=""
            className="h-[128px] w-[74px] rounded-xl object-cover shadow-lg ring-2 ring-white md:h-[190px] md:w-[110px]"
          />
          <span className="absolute -right-2 -top-2 inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary shadow-sm md:px-2 md:text-[11px]">
            <Sparkles className="h-3 w-3" strokeWidth={2.25} />
            Con tu estilo
          </span>
        </div>
      </Figura>
    </div>

    {/* ── Qué es y qué sale. */}
    <div className="mx-auto mt-5 max-w-[620px] text-center md:mt-6">
      <p className="text-[16px] font-semibold text-brand-black md:text-[17px]">Enseñale a Atlas el estilo de tu marca</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-[#02062B]/[0.62] md:text-[13.5px]">
        Guardá en carpetas las fotos que te inspiran y sumalas a tu pedido. Atlas toma de ellas la luz, la paleta, el lugar y
        la actitud, y crea las fotos de tus productos con esa estética. No copia las personas, la ropa ni los productos de las
        referencias.
      </p>
    </div>
    {onCrear && <CrearPrimeraCarpeta onCrear={onCrear} />}
    <ol className={`mx-auto mt-5 grid max-w-[760px] gap-2 ${angosto ? '' : 'md:grid-cols-3 md:gap-3'}`}>
      {PASOS.map((paso, i) => (
        <li key={paso.titulo} className="flex gap-2.5 rounded-xl bg-white p-3 text-left shadow-[0_1px_2px_rgba(2,6,43,0.05)]">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#1644EB1A] text-[12px] font-semibold text-brand-primary">
            {i + 1}
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-semibold text-brand-black">{paso.titulo}</span>
            <span className="block text-[12px] leading-snug text-[#02062B]/[0.55]">{paso.detalle}</span>
          </span>
        </li>
      ))}
    </ol>
  </div>
);

export default VacioDeEstilos;
